// ── THE FARMER'S BASE ACRES ON ONE PAGE ─────────────────────────────────────────────────
//
// Kevin, 2026-10-26: "allow us to print a one page PDF that summarizes the farmers base acres
// in a nice report." It prints baseAcreModel() (src/fsa156ez.js) — the same model the screen
// renders — so the sheet and the page cannot state two different totals.
//
// ⚠ ONE PAGE, WHATEVER THE FARMER. Du Mond carries 27 FSA farms. The sheet is laid out at the
// printable width of a portrait letter page (7.5in × 10in at 0.5in margins) with fixed row
// heights, and a farmer with more farms than one column holds is set in TWO side-by-side
// columns rather than run onto a second sheet. A fit script then scales the whole page down
// (never below FIT_MIN) if the browser's own metrics still overflow. The row budget is
// asserted offline by tools/check-fsa-156ez.js; the browser measurement records itself on
// <html data-fit-height / data-fit-zoom> so check-screens --print can read it.
//
// ⚠ A DEFAULT IS NOT AN ELECTION. Each crop cell carries the program and an asterisk when
// the program is FSA's DEFAULT rather than one the producer elected, and the footnote says so.
//
// CommonJS, no browser APIs — App.jsx imports it and the checker requires it as-is.

const PRINT_W_IN = 7.5, PRINT_H_IN = 10;
const FIT_MIN = 0.72;
const MIN_ROW_PX = 14;
const ROW_PX = 19;

const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const ac = (v) => (v == null || v === "" ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const ac0 = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 }));
const title = (c) => String(c || "").charAt(0) + String(c || "").slice(1).toLowerCase();
const CROP_SHORT = { CORN: "Corn", SOYBEANS: "Soybeans", WHEAT: "Wheat", OATS: "Oats" };
const cropName = (c) => CROP_SHORT[c] || title(c);
const PROGRAM_SHORT = { "ARC-CO": "ARC-CO", PLC: "PLC", "ARC-IC": "ARC-IC" };
function dateLabel(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const mo = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m[2]) - 1];
  return `${mo} ${Number(m[3])}, ${m[1]}`;
}

// How many columns the farm table is set in, and the pixel height the page needs at zoom 1.
// Exported so the checker asserts the budget with the same arithmetic the sheet uses.
function layoutFor(model) {
  const n = (model.farms || []).length;
  const HEADER_PX = 92, SUMMARY_PX = 64 + 18 * Math.max(1, (model.crops || []).length), TABLE_HEAD_PX = 34, FOOT_PX = 70;
  const budget = PRINT_H_IN * 96;
  const heightAt = (cols, rowPx) => HEADER_PX + SUMMARY_PX + TABLE_HEAD_PX + Math.ceil(n / cols) * rowPx + rowPx /* total row */ + FOOT_PX;
  // One column at full row height if it fits; then two columns; then two columns at the
  // tightest legible row. Past that the fit script scales the page (never below FIT_MIN).
  let cols = 1, rowPx = ROW_PX;
  if (heightAt(1, ROW_PX) > budget) { cols = 2; if (heightAt(2, ROW_PX) > budget) rowPx = MIN_ROW_PX; }
  return { cols, perCol: Math.ceil(n / cols), rowPx, height: heightAt(cols, rowPx), budget };
}

// COMPACT is the two-column (or 5+ crop) sheet: base acres and a one-letter program per cell,
// with the PLC yields left to the crop summary and the screen — a dense sheet that still fits
// one page beats a full one that does not.
const PROGRAM_LETTER = { "ARC-CO": "A", PLC: "P", "ARC-IC": "I" };
function cropCell(c, compact) {
  if (!c) return `<td class="n mute">—</td>`;
  const lbl = compact ? (PROGRAM_LETTER[c.program] || c.program) : (PROGRAM_SHORT[c.program] || c.program);
  const prog = c.program ? `<span class="pg${compact ? " pc" : ""}${c.source === "default" ? " df" : ""}">${esc(lbl)}${c.source === "default" ? "*" : ""}</span>` : "";
  return `<td class="n">${ac(c.base_acres)}${compact ? "" : `<span class="py">${c.plc_yield == null ? "" : esc(c.plc_yield)}</span>`}${prog}</td>`;
}

function farmTable(model, farms, withTotal, compact) {
  const crops = model.crops;
  const head = `<tr><th>FSA farm</th><th>County</th><th class="n">${compact ? "Crop-<br>land" : "Cropland"}</th>${crops.map((c) => `<th class="n">${esc(cropName(c))}${compact ? "" : `<span class="py">PLC yld</span>`}</th>`).join("")}<th class="n">Base total</th></tr>`;
  const body = farms.map((f) => `<tr><td class="fn">${esc(f.farm_number)}</td><td>${esc(title(f.admin_county))}${f.out_of_county.length ? `<span class="oc">${compact ? "+" + f.out_of_county.length : "+ " + esc(f.out_of_county.map(title).join(", "))}</span>` : ""}</td><td class="n">${ac(f.cropland)}</td>${crops.map((c) => cropCell(f.byCrop[c], compact)).join("")}<td class="n b">${Object.keys(f.byCrop).length ? ac(f.total_base) : `<span class="mute">no base</span>`}</td></tr>`).join("");
  const total = withTotal ? `<tr class="tot"><td colspan="2">Total · ${model.farm_count} farms</td><td class="n">${ac(model.cropland)}</td>${crops.map((c) => { const t = model.totals.find((x) => x.crop === c); return `<td class="n">${t ? ac(t.base_acres) : "—"}</td>`; }).join("")}<td class="n b">${ac(model.total_base)}</td></tr>` : "";
  return `<table class="ft">${head}${body}${total}</table>`;
}

function render156ezSummaryHtml(model, { farmerName, logoSrc, printedOn } = {}) {
  const lay = layoutFor(model);
  const compact = lay.cols > 1 || (model.crops || []).length > 4;
  const farms = model.farms || [];
  const cols = [];
  for (let i = 0; i < lay.cols; i++) cols.push(farms.slice(i * lay.perCol, (i + 1) * lay.perCol));
  const prepared = dateLabel(model.prepared_date);
  const summary = model.totals.length
    ? model.totals.map((t) => `<tr><td>${esc(cropName(t.crop))}</td><td class="n b">${ac(t.base_acres)}</td><td class="n">${t.plc_yield_weighted == null ? "—" : ac0(t.plc_yield_weighted)}</td><td class="n">${t.farms}</td></tr>`).join("")
    : `<tr><td colspan="4" class="mute">This record carries no base acres.</td></tr>`;
  const programLine = (() => {
    const by = {};
    for (const f of farms) for (const c of Object.keys(f.byCrop)) {
      const x = f.byCrop[c]; if (!x.program) continue;
      const k = x.program + (x.source === "default" ? " (FSA default)" : " (elected)");
      (by[k] = by[k] || new Set()).add(cropName(c));
    }
    const ks = Object.keys(by).sort();
    return ks.length ? ks.map((k) => `<b>${esc(k)}</b>: ${esc([...by[k]].join(", "))}`).join(" &nbsp;·&nbsp; ") : "No ARC/PLC election or default is printed on this record.";
  })();
  const warn = model.identity_ok === false || model.unparsed_count
    ? `<div class="warn">This record did not fully reconcile when it was read${model.identity_failures && model.identity_failures.length ? ` (${esc(model.identity_failures.length)} check${model.identity_failures.length === 1 ? "" : "s"} failed)` : ""}${model.unparsed_count ? ` · ${esc(model.unparsed_count)} line(s) unread` : ""}. Check the figures against the FSA-156EZ.</div>` : "";
  return `<!doctype html><html><head><meta charset="utf-8">
<title>Base acres ${esc(model.crop_year || "")} — ${esc(farmerName || "")}</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; }
  body { margin: 0; font: 10px/1.3 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1f2a1f; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  #pg { width: ${PRINT_W_IN}in; }
  .page { break-inside: avoid; }
  .brand { display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid #2d4a2d; padding-bottom: 6px; height: 46px; }
  .brand img { height: 36px; }
  .brand .bs { font-size: 8.5px; color: #60735f; text-align: right; line-height: 1.35; }
  h1 { font: 700 17px/1.2 Georgia, "Lora", serif; margin: 8px 0 1px; color: #1a2e1a; }
  .sub { font-size: 10px; color: #5a6a5a; margin-bottom: 6px; }
  .top { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 7px; }
  .kpis { display: flex; gap: 6px; flex: 0 0 auto; }
  .kpi { border: 1px solid #dde7dd; border-radius: 6px; padding: 4px 8px; min-width: 74px; background: #f7faf7; }
  .kpi .l { font-size: 7.5px; text-transform: uppercase; letter-spacing: .06em; color: #677167; font-weight: 700; }
  .kpi .v { font-size: 14px; font-weight: 800; color: #1a2e1a; font-variant-numeric: tabular-nums; }
  table { border-collapse: collapse; width: 100%; font-variant-numeric: tabular-nums; }
  .sum { flex: 1 1 auto; }
  .sum th, .sum td { padding: 2px 6px; border-bottom: 1px solid #e4ece4; height: 18px; }
  th { font-size: 7.5px; text-transform: uppercase; letter-spacing: .05em; color: #677167; text-align: left; font-weight: 700; border-bottom: 1.5px solid #2d4a2d; padding: 3px 4px; vertical-align: bottom; }
  .n { text-align: right; } .b { font-weight: 800; } .mute { color: #8a968a; }
  .prog { font-size: 9px; color: #3a4a3a; margin: 0 0 6px; line-height: 1.4; }
  .cols { display: flex; gap: 10px; }
  .cols > div { flex: 1 1 0; min-width: 0; }
  .ft td { height: ${lay.rowPx}px; padding: 0 4px; border-bottom: 1px solid #edf2ed; white-space: nowrap; font-size: ${lay.cols > 1 ? 8.5 : 9.5}px; }
  .ft tr:nth-child(even) td { background: #f9fbf9; }
  .ft .fn { font-weight: 700; color: #1a2e1a; }
  .ft .py { display: inline-block; min-width: 24px; margin-left: 4px; color: #60735f; font-size: 0.9em; text-align: right; }
  th .py { display: block; margin: 0; color: #8a968a; text-transform: none; letter-spacing: 0; font-weight: 600; }
  .ft .pg { display: inline-block; width: 40px; margin-left: 5px; font-size: 0.82em; font-weight: 700; color: #2d6a2d; text-align: left; }
  .ft .pg.df { color: #8a6d1f; }
  .ft .pg.pc { width: 14px; margin-left: 3px; }
  .cols.c2 .ft td, .cols.c2 .ft th { padding: 0 2px; }
  .ft .oc { margin-left: 4px; color: #8a6d1f; font-size: 0.88em; }
  .ft .tot td { border-top: 1.5px solid #2d4a2d; font-weight: 800; background: #f2f6f2 !important; }
  .warn { border: 1px solid #e6c98a; background: #fdf8ec; color: #6b4f12; padding: 4px 8px; border-radius: 5px; margin-bottom: 6px; font-size: 9px; }
  .foot { margin-top: 8px; font-size: 8px; color: #60735f; line-height: 1.45; border-top: 1px solid #dde7dd; padding-top: 5px; }
</style></head><body><div id="pg"><section class="page">
  <div class="brand">${logoSrc ? `<img src="${esc(logoSrc)}" alt="Clover Ag Risk Management" />` : `<b>Clover Ag Risk Management</b>`}<div class="bs">Clover Ag Risk Management · Spartan Insurance Agency LLC<br>(585) 233-1066 · office@cloverag.com</div></div>
  <h1>Base Acres &amp; PLC Yields · ${esc(model.crop_year || "")}</h1>
  <div class="sub"><b>${esc(farmerName || "")}</b> &nbsp;·&nbsp; Source: FSA-156EZ Abbreviated 156 Farm Record${prepared ? `, prepared by FSA ${esc(prepared)}` : ""}${printedOn ? ` &nbsp;·&nbsp; printed ${esc(printedOn)}` : ""}</div>
  ${warn}
  <div class="top">
    <div class="kpis">
      <div class="kpi"><div class="l">Base acres</div><div class="v">${ac(model.total_base)}</div></div>
      <div class="kpi"><div class="l">FSA farms</div><div class="v">${esc(model.farm_count)}</div></div>
      <div class="kpi"><div class="l">Tracts</div><div class="v">${esc(model.tract_count)}</div></div>
    </div>
    <table class="sum"><tr><th>Crop</th><th class="n">Base acres</th><th class="n">PLC yield (base-wtd)</th><th class="n">FSA farms</th></tr>${summary}</table>
  </div>
  <div class="prog">${programLine}</div>
  <div class="cols${lay.cols > 1 ? " c2" : ""}">${cols.map((c, i) => `<div>${farmTable(model, c, i === cols.length - 1, compact)}</div>`).join("")}</div>
  <div class="foot">
    ${compact ? "Each crop cell shows base acres and the ARC/PLC program — <b>A</b> = ARC-CO, <b>P</b> = PLC, <b>I</b> = ARC-IC; per-farm PLC yields are on the Coverage Advisor screen and in the CSV." : "Each crop cell shows base acres, the FSA farm's PLC yield, and its ARC/PLC program."} <b>* = FSA default</b>: the program FSA applies when the producer made no election — it is not an election${model.any_ccc866 ? "; a program without * on a farm with a signed CCC-866 is that signed election." : ""} the producer made.
    PLC yield in the crop summary is weighted by base acres across FSA farms, so it is a summary of several FSA farms' yields, not one FSA figure.
    “+ county” marks tracts physically located outside the farm's administrative county (ARC-CO follows the tract's physical location).
    Figures are as printed on the FSA record; verify with the county FSA office before acting on them.
  </div>
</section></div><div id="pg-end"></div>
<script>
(function () {
  var P = document.getElementById("pg"), E = document.getElementById("pg-end");
  if (!P || !E || !("zoom" in P.style)) return;
  var W = ${PRINT_W_IN} * 96, H = ${PRINT_H_IN} * 96, z = 1;
  function set(t) { z = t; P.style.zoom = t; P.style.width = (W / t) + "px"; }
  function fits(t) { set(t); return E.offsetTop <= H; }
  var lo = ${FIT_MIN}, hi = 1;
  if (fits(hi)) lo = hi;
  else { for (var i = 0; i < 10; i++) { var mid = (lo + hi) / 2; if (fits(mid)) lo = mid; else hi = mid; } }
  set(lo);
  document.documentElement.setAttribute("data-fit-zoom", lo.toFixed(3));
  document.documentElement.setAttribute("data-fit-height", String(E.offsetTop));
})();
</script>
</body></html>`;
}

module.exports = { render156ezSummaryHtml, layoutFor, PRINT_W_IN, PRINT_H_IN, FIT_MIN, ROW_PX, MIN_ROW_PX };
